import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';

import { useStyles } from './styles';
import VerticalTabsMenu from '../ProfilesTabs/VerticalTabsMenu';
import ITemperatures from '../ProfilesTabs/interfaces/Temperatures';
import { TabPanel } from '../ProfilesTabs/TabPanel';
import { ShowOnlyProfile } from '../ProfilesTabs/ShowOnlyProfile';
import { HoldingProfileForm } from '../ProfilesTabs/AddNewHoldingProfile';
import { Button, Grid } from '@material-ui/core';
import { AvailabilitySelector } from '../ProfilesTabs/AvailabilitySelector';
import { InitializeTemperature } from '../ProfilesTabs/helpers/InitializeTemperature';
import { isDisabledSaveButtonEdit } from '../ProfilesTabs/helpers/isDisabledSaveButton';
import { TProductSheet } from '../../types/product';
import { InitializeProductSheet } from '../ProfilesTabs/helpers/InitializeProductSheet';
import { isSaveButtonEnabled } from '../ProfilesTabs/helpers/isSaveButtonEnabled';
import {
  getCountries as getCountriesByRegion,
  getRegions,
  getStates,
} from '../../redux/actions/regionsActions';
import { getEntities as getShelfUnits } from '../../redux/actions/shelfUnitsActions';
import { getEntities as getUnitsOfMeasures } from '../../redux/actions/unitsOfMeasuresActions';
import {
  createMenuItemsHoldingProfiles,
  removeMenuItemsHoldingProfiles,
  updateMenuItemsHoldingProfiles,
} from '../../redux/actions/menuItemsActions';
import { snackbarShowMessage } from '../../redux/actions/snackbarActions';
import { BRANDS } from '../../types/brands';
import { VIEW_MODE_STATES } from '../EquipmentHoldingProfile/utils';
import { getDefaultLabel, toCamelCase } from '../../utils/helpers';
import { transformProductSheetToApiRequest } from '../../utils/transformers/productSheet';
import { productGroupsErrors } from '../../utils/constants/errors';
import { RAMError } from '../../utils/RAMError';

type TGetPayload = {
  region: string;
  country: string;
  state: string;
  cookoutTemperatures: any;
  cookoutReq: boolean;
  holdingReq: boolean;
  holdingTemperatures: any;
  productSheet: TProductSheet;
  brand: string;
};

function getPayload({
  region,
  country,
  state,
  cookoutTemperatures,
  cookoutReq,
  holdingReq,
  holdingTemperatures,
  productSheet,
  brand,
}: TGetPayload) {
  const payload: any = {
    region: region !== 'all' ? region : 'GLOBAL',
    country: country !== 'all' ? country : null,
    state: state !== 'all' ? state : null,
    heatingDefault: false,
    heatingCookout: true,
    heatingMin: cookoutReq
      ? +Number(cookoutTemperatures.tempMinF).toFixed(1)
      : null,
    heatingMax: cookoutReq
      ? +Number(cookoutTemperatures.tempMaxF).toFixed(1)
      : null,
    heating: cookoutReq,
    holding: holdingReq,
    holdingMin: holdingReq
      ? +Number(holdingTemperatures.tempMinF).toFixed(1)
      : null,
    holdingMax: holdingReq
      ? +Number(holdingTemperatures.tempMaxF).toFixed(1)
      : null,
  };
  if (brand === BRANDS.TH) {
    payload.productSheet = transformProductSheetToApiRequest(productSheet);
  }
  return payload;
}

export default function VerticalTabsForMenuItems({
  setViewMode,
  refreshOnSave,
  viewMode,
  readOnly,
}) {
  const classes = useStyles();
  const [tabValue, setTabValue] = React.useState(0);
  const [cookoutReq, setCookoutReq] = useState(true);
  const [holdingReq, setHoldingReq] = useState(true);
  const [holdingTemperatures, setHoldingTemperatures] = useState<ITemperatures>(
    InitializeTemperature(),
  );
  const [cookoutTemperatures, setCookoutTemperatures] = useState<ITemperatures>(
    InitializeTemperature(),
  );
  const [productSheet, setProductSheet] = useState<TProductSheet>(
    InitializeProductSheet(),
  );

  //dropdown handling
  const [region, setRegion] = useState('all');
  const [country, setCountry] = useState('all');
  const [countryState, setCountryState] = useState('all');
  const [verticalTabDefaultLabel, setVerticalTabDefaultLabel] = useState('');
  const [temperatureErrors, setTemperatureErrors] = useState({});
  const [cookoutErrors, setCookoutErrors] = useState({});
  const [holdingErrors, setHoldingErrors] = useState({});
  const dispatch: any = useDispatch();
  const { profiles, menuItem } = useSelector(
    (state: RootStateOrAny) => state.MenuItemsReducer,
  );
  const { countries, regions, states } = useSelector(
    (state: RootStateOrAny) => state.RegionsReducer,
  );
  const { thisUser } = useSelector(
    (state: RootStateOrAny) => state.UserReducer,
  );
  const unitsOfMeasures = useSelector(
    (state: RootStateOrAny) => state.UnitsOfMeasuresReducer.entities,
  );
  const [selectedProfileIndex, setSelectedProfileIndex] = useState(0);
  useEffect(() => {
    if (viewMode === VIEW_MODE_STATES.CREATE) {
      setSelectedProfileIndex(profiles.length);
      setTabValue(profiles.length);
      setProductSheet(InitializeProductSheet());
      setCookoutTemperatures(InitializeTemperature());
      setHoldingTemperatures(InitializeTemperature());
      setHoldingReq(true);
      setCookoutReq(true);
    }
  }, [viewMode]);

  const { selectedBrand } = useSelector(
    (state: RootStateOrAny) => state.UIReducer,
  );
  useEffect(() => {
    setVerticalTabDefaultLabel(
      getDefaultLabel(countryState, country, region, countries, states),
    );
  }, [countryState, country, region, states, countries, regions]);

  function handleCancel() {
    setViewMode(VIEW_MODE_STATES.READ);
    setTabValue(0);
    setRegion('all');
    setCookoutErrors({});
    setHoldingErrors({});
  }

  useEffect(() => {
    if (region !== 'all') {
      dispatch(getCountriesByRegion(region));
    }
  }, [region]);
  useEffect(() => {
    if (country !== 'all') {
      dispatch(getStates(region, country));
    }
  }, [country]);
  const handleChangeSelectRegion = selected => {
    setRegion(selected);
  };
  const handleChangeSelectCountry = selected => {
    setCountry(selected);
  };
  const handleChangeSelectState = selected => {
    setCountryState(selected);
  };

  const handleError = error => {
    if (error instanceof RAMError) {
      const errorCode = toCamelCase(error.code);
      const messageKey = `productGroups.errors.${errorCode}`;
      let message = t(messageKey);
      if (!message || messageKey === message) {
        message = productGroupsErrors[errorCode] || `ERROR: ${error.message}`;
      } else if (
        !!error.params?.field?.length &&
        (errorCode === 'minMaxTemperature' ||
          errorCode === 'temperatureOutOfRange')
      ) {
        const displayField =
          error.params?.field[0].toUpperCase() + error.params?.field.slice(1);
        message = `${displayField} ${message}`;
      }
      dispatch(snackbarShowMessage(message));
    } else {
      dispatch(snackbarShowMessage(error.message));
    }
  };

  const cleanForm = () => {
    refreshOnSave();
    setViewMode(VIEW_MODE_STATES.READ);
    setRegion('all');
    setCountry('all');
    setCountryState('all');
    setTabValue(0);
    setProductSheet(InitializeProductSheet());
  };

  const validateTemperatures = temperatures => {
    let isValid = true;
    let tempTemperatureErrors = { ...temperatureErrors };

    Object.entries(temperatures).forEach(([field, values]: [string, any]) => {
      const min = Number(values.tempMinF);
      const max = Number(values.tempMaxF);
      if (min <= 0.0 || max <= 0.0 || min > 999.9 || max > 999.9) {
        handleError(new RAMError('TEMPERATURE_OUT_OF_RANGE', '', { field }));
        tempTemperatureErrors = {
          ...tempTemperatureErrors,
          [field]: { minMax: true },
        };
        isValid = false;
      } else if (min >= max) {
        handleError(new RAMError('MIN_MAX_TEMPERATURE', '', { field }));
        tempTemperatureErrors = {
          ...tempTemperatureErrors,
          [field]: { minMax: true },
        };
        isValid = false;
      } else {
        isValid = isValid && true;
      }
    });
    setTemperatureErrors(tempTemperatureErrors);

    return isValid;
  };

  const createNewProfile = async () => {
    const payload = getPayload({
      region,
      country,
      state: countryState,
      cookoutTemperatures,
      cookoutReq,
      holdingReq,
      holdingTemperatures,
      productSheet,
      brand: selectedBrand,
    });

    const result: any = await dispatch(
      createMenuItemsHoldingProfiles(menuItem.id, selectedBrand, payload),
    );
    if (result.status < 300) {
      dispatch(snackbarShowMessage('productGroups.productProfileCreated'));
      cleanForm();
    }
  };

  const editProfile = async () => {
    const selectedProfile = profiles[tabValue];
    const payload = getPayload({
      region,
      country,
      state: countryState,
      cookoutTemperatures,
      cookoutReq,
      holdingReq,
      holdingTemperatures,
      productSheet,
      brand: selectedBrand,
    });

    await dispatch(
      updateMenuItemsHoldingProfiles(
        menuItem.id,
        selectedProfile.id,
        selectedBrand,
        payload,
      ),
    );
    dispatch(snackbarShowMessage('productGroups.productProfileSaved'));
    cleanForm();
  };

  const handleSubmitProfile = async () => {
    const temperatures = [
      {
        type: 'cookout',
        req: cookoutReq,
        values: cookoutTemperatures,
      },
      {
        type: 'holding',
        req: holdingReq,
        values: holdingTemperatures,
      },
    ];
    if (viewMode === VIEW_MODE_STATES.CREATE) {
      await createNewProfile();
    } else if (viewMode === VIEW_MODE_STATES.EDIT) {
      await editProfile();
    }
  };

  useEffect(() => {
    if (selectedBrand) {
      dispatch(getRegions());
    }
    if (selectedBrand === BRANDS.TH) {
      dispatch(getShelfUnits());
      dispatch(getUnitsOfMeasures());
    }
  }, [dispatch, selectedBrand]);
  const handleDelete = index => () => {
    const selectedProfile = profiles[index];
    dispatch(
      removeMenuItemsHoldingProfiles(menuItem.id, selectedProfile.id),
    ).then(() => {
      refreshOnSave();
      setTabValue(0);
    });
  };

  const { t } = useTranslation();

  const handleOpenEditMode = index => () => {
    setViewMode(VIEW_MODE_STATES.EDIT);
    setSelectedProfileIndex(index);
    const selectedProfile = profiles[index];
    setHoldingTemperatures({
      tempMin: selectedProfile.holdingTemperature?.minC,
      tempMax: selectedProfile.holdingTemperature?.maxC,
      tempMinF: selectedProfile.holdingTemperature?.minF,
      tempMaxF: selectedProfile.holdingTemperature?.maxF,
    });
    setCookoutTemperatures({
      tempMin: selectedProfile.cookoutTemperature?.minC,
      tempMax: selectedProfile.cookoutTemperature?.maxC,
      tempMinF: selectedProfile.cookoutTemperature?.minF,
      tempMaxF: selectedProfile.cookoutTemperature?.maxF,
    });
    setTabValue(index);
    const selectedProductSheet = selectedProfile.productSheet;
    setProductSheet({
      display: Boolean(selectedProductSheet?.onProductionSheet),
      fresh: Boolean(selectedProductSheet?.fresh),
      shelfLife: Number(selectedProductSheet?.shelfLife),
      shelfLifeUnit: Number(selectedProductSheet?.shelfLifeUnit),
      unitsOfMeasure: {
        units: (selectedProductSheet?.unitsOfMeasure?.units || []).map(unitId =>
          unitsOfMeasures.find(unit => unit.id === unitId),
        ),
        default: selectedProductSheet?.unitsOfMeasure?.default || 0,
      },
    });
    setCookoutReq(selectedProfile.cookoutTemperature?.display);
    setHoldingReq(selectedProfile.holdingTemperature?.display);
    setRegion(selectedProfile.region);
    setCountry(selectedProfile?.country_id || 'all');
    setCountryState(selectedProfile.state_id || 'all');
  };
  const temperatureObj = [
    {
      temperatures: cookoutTemperatures,
      isRequired: cookoutReq,
      errors: cookoutErrors,
    },
    {
      temperatures: holdingTemperatures,
      isRequired: holdingReq,
      errors: holdingErrors,
    },
  ];

  const getPropertiesToRender = profile => {
    const properties: any[] = [];
    if (profile?.cookoutTemperature && profile?.cookoutTemperature?.display)
      properties.push({
        displayTitle:
          selectedBrand === BRANDS.TH
            ? t('commodities.initialHeatingTemperature')
            : t('commodities.cookoutProfileTemperatures'),
        temperatureName: 'cookoutTemperature',
      });

    if (profile?.holdingTemperature && profile?.holdingTemperature?.display)
      properties.push({
        displayTitle: t('productGroups.viewDetail.holdingTemperature'),
        temperatureName: 'holdingTemperature',
      });

    return properties;
  };

  return (
    <div className={classes.root}>
      <VerticalTabsMenu
        profiles={profiles}
        value={tabValue}
        setValue={setTabValue}
        defaultName={verticalTabDefaultLabel}
        hideDefault={VIEW_MODE_STATES.CREATE !== viewMode}
        disabled={
          viewMode === VIEW_MODE_STATES.EDIT ||
          viewMode === VIEW_MODE_STATES.CREATE
        }
      />
      {viewMode !== VIEW_MODE_STATES.READ && (
        <TabPanel value={tabValue} index={selectedProfileIndex}>
          <Grid container>
            <AvailabilitySelector
              name={t('productGroups.viewDetail.productProfileAvailability')}
              regions={regions}
              region={region}
              handleChangeSelectRegion={handleChangeSelectRegion}
              countries={countries.map(mappedCountry => ({
                id: mappedCountry.id,
                name: t(
                  `app.common.countries.${mappedCountry.iso3}`,
                  mappedCountry.description,
                ),
              }))}
              country={country}
              handleChangeSelectCountry={handleChangeSelectCountry}
              states={states.map(mappedState => ({
                id: mappedState.id,
                name: mappedState.description,
              }))}
              state={countryState}
              handleChangeSelectState={handleChangeSelectState}
              isAllDisabled={viewMode === VIEW_MODE_STATES.EDIT}
            />
            <HoldingProfileForm
              formProfiles={[
                {
                  name:
                    selectedBrand === BRANDS.TH
                      ? t('commodities.initialHeatingTemperature')
                      : t('commodities.cookoutProfileTemperatures'),
                  isRequired: cookoutReq,
                  setIsRequired: setCookoutReq,
                  temperatures: cookoutTemperatures,
                  setTemperatures: setCookoutTemperatures,
                  errorValidations: cookoutErrors,
                  setErrors: setCookoutErrors,
                },
                {
                  name: t('productGroups.viewDetail.holdingTemperature'),
                  isRequired: holdingReq,
                  setIsRequired: setHoldingReq,
                  temperatures: holdingTemperatures,
                  setTemperatures: setHoldingTemperatures,
                  errorValidations: holdingErrors,
                  setErrors: setHoldingErrors,
                },
              ]}
              productSheet={productSheet}
              setProductSheet={setProductSheet}
            />
            <Grid item xs={12} style={{ marginTop: 10, textAlign: 'right' }}>
              <Button
                style={{ margin: 3 }}
                variant="outlined"
                onClick={handleCancel}
              >
                {t('app.common.cancel')}
              </Button>
              <Button
                disabled={
                  !isSaveButtonEnabled(
                    temperatureObj,
                    productSheet,
                    selectedBrand,
                  ) ||
                  isDisabledSaveButtonEdit(
                    temperatureObj,
                    cookoutErrors,
                    holdingErrors,
                  )
                }
                style={{ margin: 3 }}
                variant="outlined"
                onClick={handleSubmitProfile}
              >
                {t('app.common.save')}
              </Button>
            </Grid>
          </Grid>
        </TabPanel>
      )}
      {viewMode === VIEW_MODE_STATES.READ &&
        profiles?.map((profile, index) => (
          <TabPanel value={tabValue} index={index} key={index}>
            <ShowOnlyProfile
              title={t('productGroups.viewDetail.productProfileAvailability')}
              canDelete={
                thisUser.hasHoldingProfileMenuItem &&
                !profile.is_default &&
                !readOnly
              }
              handleDelete={handleDelete}
              classes={classes}
              canEdit={thisUser.hasHoldingProfileMenuItem && !readOnly}
              profile={profile}
              handleOpenEditMode={handleOpenEditMode}
              index={index}
              propertiesToRender={getPropertiesToRender(profile)}
              isDeleteDisabled={profiles.length < 2}
            />
          </TabPanel>
        ))}
    </div>
  );
}
